import {
  BoxGeometry,
  CircleGeometry,
  CylinderGeometry,
  Group,
  Mesh,
  MeshStandardMaterial,
  SphereGeometry,
  TorusGeometry,
} from 'three';
import { FixtureKind, RoomFixture } from '../../shared/types';

const porcelain = (roughness = 0.18) =>
  new MeshStandardMaterial({ color: '#f4f4f2', roughness, metalness: 0.04 });

const metal = () =>
  new MeshStandardMaterial({ color: '#c9ccd1', roughness: 0.25, metalness: 0.85 });

const add = (group: Group, mesh: Mesh) => {
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  group.add(mesh);
  return mesh;
};

/** Ванна: внешний борт + чаша + слив + смеситель. Размеры ~1700×750×560 мм */
export const buildBath = (): { group: Group; size: { w: number; d: number; h: number } } => {
  const group = new Group();
  const W = 1.7, D = 0.75, H = 0.56, wall = 0.07;
  // Внешний корпус
  add(group, new Mesh(new BoxGeometry(W, H, D), porcelain()));
  // Внутренняя чаша (вычитаем визуально тёмной вставкой)
  const inner = new Mesh(new BoxGeometry(W - wall * 2, H - wall * 0.5, D - wall * 2), new MeshStandardMaterial({ color: '#ffffff', roughness: 0.12 }));
  inner.position.y = wall;
  add(group, inner);
  // Скругление борта — тор по периметру верха
  const rim = new Mesh(new TorusGeometry(0.015, 0.015, 8, 4), porcelain());
  rim.rotation.x = Math.PI / 2;
  rim.visible = false;
  group.add(rim);
  // Слив
  const drain = new Mesh(new CircleGeometry(0.03, 16), metal());
  drain.rotation.x = -Math.PI / 2;
  drain.position.set(-W / 2 + 0.18, H - wall * 0.5 + 0.002, 0);
  add(group, drain);
  // Смеситель на борту
  const pipe = new Mesh(new CylinderGeometry(0.018, 0.018, 0.22, 12), metal());
  pipe.position.set(-W / 2 + 0.12, H + 0.11, -D / 2 + 0.12);
  add(group, pipe);
  const spout = new Mesh(new CylinderGeometry(0.014, 0.014, 0.16, 12), metal());
  spout.rotation.z = Math.PI / 2;
  spout.position.set(-W / 2 + 0.2, H + 0.2, -D / 2 + 0.12);
  add(group, spout);
  return { group, size: { w: W, d: D, h: H } };
};

/** Унитаз: ножка + чаша + бачок + сиденье. Компакт ~360×650×770 мм */
export const buildToilet = (): { group: Group; size: { w: number; d: number; h: number } } => {
  const group = new Group();
  // Ножка/пьедестал
  const base = new Mesh(new CylinderGeometry(0.11, 0.14, 0.4, 16), porcelain());
  base.position.y = 0.2;
  add(group, base);
  // Чаша
  const bowl = new Mesh(new SphereGeometry(0.19, 20, 14), porcelain(0.14));
  bowl.scale.set(1, 0.62, 1.15);
  bowl.position.set(0, 0.47, 0.07);
  add(group, bowl);
  // Сиденье
  const seat = new Mesh(new TorusGeometry(0.16, 0.028, 10, 22), new MeshStandardMaterial({ color: '#ffffff', roughness: 0.3 }));
  seat.rotation.x = Math.PI / 2;
  seat.scale.set(1, 1.12, 1);
  seat.position.set(0, 0.585, 0.07);
  add(group, seat);
  // Бачок
  const tank = new Mesh(new BoxGeometry(0.36, 0.36, 0.16), porcelain());
  tank.position.set(0, 0.62, -0.2);
  add(group, tank);
  // Кнопка слива
  const button = new Mesh(new CylinderGeometry(0.03, 0.03, 0.02, 12), metal());
  button.position.set(0, 0.81, -0.2);
  add(group, button);
  return { group, size: { w: 0.36, d: 0.65, h: 0.77 } };
};

/** Раковина: пьедестал + чаша + смеситель. ~500×420×800 мм */
export const buildSink = (): { group: Group; size: { w: number; d: number; h: number } } => {
  const group = new Group();
  // Пьедестал
  const pedestal = new Mesh(new CylinderGeometry(0.06, 0.09, 0.62, 16), porcelain());
  pedestal.position.y = 0.31;
  add(group, pedestal);
  // Чаша (полусфера перевёрнутая)
  const bowl = new Mesh(new SphereGeometry(0.23, 20, 12, 0, Math.PI * 2, Math.PI / 2, Math.PI / 2), porcelain(0.14));
  bowl.scale.set(1, 0.55, 0.85);
  bowl.position.y = 0.72;
  add(group, bowl);
  // Столешница-бортик
  const deck = new Mesh(new CylinderGeometry(0.24, 0.24, 0.03, 24), porcelain());
  deck.scale.set(1, 1, 0.85);
  deck.position.y = 0.815;
  add(group, deck);
  // Смеситель
  const pipe = new Mesh(new CylinderGeometry(0.015, 0.015, 0.24, 12), metal());
  pipe.position.set(0, 0.93, -0.17);
  add(group, pipe);
  const spout = new Mesh(new CylinderGeometry(0.012, 0.012, 0.14, 12), metal());
  spout.rotation.x = Math.PI / 2.6;
  spout.position.set(0, 1.02, -0.1);
  add(group, spout);
  return { group, size: { w: 0.5, d: 0.42, h: 1.0 } };
};

const builders: Record<FixtureKind, () => { group: Group; size: { w: number; d: number; h: number } }> = {
  bath: buildBath,
  toilet: buildToilet,
  sink: buildSink,
};

/** Возвращает группу сантехники, размещённую по координатам комнаты (мм → м) */
export const buildFixture = (fixture: RoomFixture): { group: Group; footprint: { w: number; d: number } } => {
  const { group, size } = builders[fixture.kind]();
  group.rotation.y = (fixture.rotation * Math.PI) / 180;
  // Позиция: x/z — центр сантехники в мм от угла комнаты
  group.position.set(fixture.x / 1000, 0, fixture.z / 1000);
  const rotated = fixture.rotation === 90 || fixture.rotation === 270;
  const footprint = rotated
    ? { w: size.d, d: size.w }
    : { w: size.w, d: size.d };
  return { group, footprint };
};

/** Габариты сантехники в мм — для обрезки по границам комнаты и UI-подсказок */
export const fixtureSizeMm: Record<FixtureKind, { w: number; d: number }> = {
  bath: { w: 1700, d: 750 },
  toilet: { w: 400, d: 650 },
  sink: { w: 500, d: 450 },
};
